import React, { useState, useEffect } from "react";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { ChevronLeft, ChevronRight, Plus, Eye, X } from "lucide-react";

interface ProfileWizardNavigationProps {
  activeStep: number;
  showPreview: boolean;
  editMode: boolean;
  isLoading: boolean;
  canGoNext: boolean;
  canGoBack: boolean;
  nextButtonText: string;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
  isDarkMode: boolean;
}

const ProfileWizardNavigation = ({
  activeStep,
  showPreview,
  editMode,
  isLoading,
  canGoNext,
  canGoBack,
  nextButtonText,
  onNext,
  onBack,
  onCancel,
  isDarkMode
}: ProfileWizardNavigationProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  const isFirstStep = activeStep === 0;
  const shouldShowCancelButton = editMode && isFirstStep && !showPreview;
  const shouldShowBackButton = !shouldShowCancelButton && (canGoBack || showPreview);

  const getNextIcon = () => {
    if (showPreview) {
      return <Plus size={20} />;
    } else if (activeStep === 2) {
      return <Eye size={20} />;
    } else {
      return <ChevronRight size={20} />;
    }
  };

  const getBackButtonText = () => {
    return showPreview ? textConfig.PROFILE_WIZARD_NAVIGATION_EDIT : textConfig.PROFILE_WIZARD_NAVIGATION_BACK;
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
      {shouldShowCancelButton ? (
        <Button
          onClick={onCancel}
          startIcon={<X size={18} />}
          sx={{
            backgroundColor: isDarkMode ? "#3F3F46" : "#E5E7EB",
            color: isDarkMode ? "#FFFFFF" : "#1F2937",
            fontSize: isMobile ? "12px" : "14px",
            "&:hover": {
              backgroundColor: isDarkMode ? "#52525B" : "#D1D5DB",
            },
          }}
        >
          {textConfig.PROFILE_WIZARD_NAVIGATION_CANCEL}
        </Button>
      ) : shouldShowBackButton ? (
        <Button
          onClick={onBack}
          disabled={!canGoBack && !showPreview}
          startIcon={<ChevronLeft size={20} />}
          sx={{
            backgroundColor: isDarkMode ? "#27272A" : "#E5E7EB",
            color: isDarkMode ? "#FFFFFF" : "#1F2937",
            fontSize: isMobile ? "12px" : "14px",
            "&:hover": {
              backgroundColor: isDarkMode ? "#3F3F46" : "#D1D5DB",
            },
            "&:disabled": {
              backgroundColor: isDarkMode ? "#18181B" : "#F3F4F6",
              color: isDarkMode ? "#52525B" : "#9CA3AF",
            }
          }}
        >
          {getBackButtonText()}
        </Button>
      ) : (
        <Box />
      )}

      <Button
        onClick={onNext}
        disabled={isLoading || !canGoNext}
        endIcon={getNextIcon()}
        sx={{
          backgroundColor: "#22C55E",
          color: "#FFFFFF",
          fontSize: isMobile ? "12px" : "14px",
          fontWeight: 600,
          px: 3,
          "&:hover": { 
            backgroundColor: "#16A34A",
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(34, 197, 94, 0.3)"
          },
          "&:disabled": {
            backgroundColor: "#6B7280",
            color: "#D1D5DB",
            transform: "none",
            boxShadow: "none"
          },
          transition: "all 0.2s ease-in-out"
        }}
      >
        {nextButtonText}
      </Button>
    </Box>
  );
};

export default ProfileWizardNavigation;